<template>
  <div class="space-y-4">
    <div>
      <label :for="blockId + '-url'" class="block text-sm font-medium text-gray-700">{{ $t('projects.embed_url_label') }}</label>
      <div class="mt-1 flex flex-col gap-2 sm:flex-row">
        <input :id="blockId + '-url'" v-model.trim="url" type="url" inputmode="url"
               :placeholder="$t('projects.embed_url_placeholder')" :aria-describedby="blockId + '-url-help'"
               @keyup.enter="showPreview"
               class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-bpblue focus:border-bpblue sm:text-sm">
        <button type="button" v-on:click="showPreview" :disabled="!canPreview"
                class="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          {{ $t('projects.embed_preview') }}
        </button>
      </div>
      <p :id="blockId + '-url-help'" class="mt-2 text-sm text-gray-600">{{ $t('projects.embed_url_help') }}</p>
    </div>

    <div class="max-w-xs">
      <label :for="blockId + '-height'" class="block text-sm font-medium text-gray-700">{{ $t('projects.embed_height_label') }}</label>
      <input :id="blockId + '-height'" v-model.number="height" type="number" inputmode="numeric"
             :min="minHeight" :max="maxHeight" step="10" :placeholder="String(defaultHeight)"
             :aria-describedby="blockId + '-height-help'"
             class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-bpblue focus:border-bpblue sm:text-sm">
      <p :id="blockId + '-height-help'" class="mt-2 text-sm text-gray-600">
        {{ $t('projects.embed_height_help', {min: minHeight, max: maxHeight}) }}
      </p>
    </div>

    <!--
      Vista previa con el `sandbox` que decide el backend (lms.embed_sandbox). Un
      bloque nuevo aún no lo tiene: la vista previa aparece al guardar la lección.
    -->
    <div v-if="previewUrl">
      <iframe v-if="sandbox" :src="previewUrl" :sandbox="sandbox" :title="$t('projects.embed_preview_title')"
              :style="{height: previewHeight + 'px'}" referrerpolicy="no-referrer" loading="lazy"
              class="block w-full max-w-3xl rounded-md border border-gray-200 bg-white"></iframe>
      <p v-else class="text-sm text-gray-600">{{ $t('projects.embed_preview_after_save') }}</p>
    </div>
  </div>
</template>

<script>
import {EMBED_DEFAULT_HEIGHT, EMBED_MAX_HEIGHT, EMBED_MIN_HEIGHT, isHttpsUrl} from "@/views/projects/ProjectEdit/Blocks/embedRules";

/*
 * Bloque de página embebida (key `embed`, LMS-073). `data` es { url, height } y
 * el backend añade `sandbox` al devolverlo. Solo admite https y los dominios de
 * `lms.embed_allowlist`, que se comprueban en el backend (422 en `data.url`).
 */
export default {
  name: "EmbedBlock",
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    },
    lessonUuid: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      minHeight: EMBED_MIN_HEIGHT,
      maxHeight: EMBED_MAX_HEIGHT,
      defaultHeight: EMBED_DEFAULT_HEIGHT,
      // Un bloque ya guardado se previsualiza al abrirlo; uno nuevo, al pedirlo.
      previewUrl: isHttpsUrl(this.modelValue.url) ? this.modelValue.url : null,
    }
  },
  computed: {
    url: {
      get() {
        return this.modelValue.url ?? ''
      },
      set(url) {
        this.$emit('update:modelValue', {...this.modelValue, url})
      }
    },
    height: {
      get() {
        return this.modelValue.height ?? ''
      },
      set(height) {
        this.$emit('update:modelValue', {...this.modelValue, height: height === '' ? null : height})
      }
    },
    sandbox() {
      return this.modelValue.sandbox ?? null
    },
    canPreview() {
      return isHttpsUrl(this.url)
    },
    previewHeight() {
      const height = Number(this.modelValue.height) || EMBED_DEFAULT_HEIGHT
      return Math.min(Math.max(height, EMBED_MIN_HEIGHT), EMBED_MAX_HEIGHT)
    }
  },
  methods: {
    showPreview() {
      if (this.canPreview) this.previewUrl = this.url
    }
  }
}
</script>
